import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '.';
import { TopicLink } from '../types';
import { API_BASE_URL } from '../config';

interface ITopic {
    _id: string;
    title: string;
    assignee: string;
    questions: string[];
    submitted?: boolean;
    blog?: string;
    published?: boolean;
    publishedUrl?: string;
    tempPublishedUrl?: string;
    userApprovalStatus?: 'pending' | 'approved' | 'rejected';
    adminApprovalStatus?: 'pending' | 'approved' | 'rejected';
    userEdited?: boolean;
    userEditedAt?: string;
}

interface SubmitFormRequest {
    topicId: string;
    answers: string[];
}

interface PublishFormResponse {
    message: string;
    filename: string;
    url: string;
}

interface PublishFormRequest {
    formId: string;
    comments?: string;
}

export interface IBacklink {
    title: string;
    url: string;
    comment: string;
    posted?: boolean;
    postedAt?: string;
}

interface BacklinksResponse {
    backlinks: IBacklink[];
}

export interface MyPublishedBlog {
    _id: string;
    title: string;
    slug: string;
    publishedUrl: string;
    publishedDate?: string;
    category?: string;
    excerpt?: string;
    featuredImage?: string;
    readTime?: string;
    updatedAt?: string;
    views?: string;
    backlinkCount: number;
}

export interface MyDraft {
    _id: string;
    title: string;
    slug?: string;
    category?: string;
    excerpt?: string;
    userApprovalStatus: 'pending' | 'approved' | 'rejected';
    adminApprovalStatus: 'pending' | 'approved' | 'rejected';
    tempPublishedUrl?: string;
    userEdited: boolean;
    userEditedAt?: string;
    updatedAt?: string;
    userApprovalToken?: string;
}

export interface AllPublishedBlog extends MyPublishedBlog {
    assignee: string;
    authorName: string;
    userEdited: boolean;
}

export interface BacklinkHubItem {
    formId: string;
    backlinkIndex: number;
    blogTitle: string;
    blogSlug: string;
    publishedUrl: string;
    title: string;
    url: string;
    comment: string;
    posted: boolean;
    postedAt?: string;
}

export interface BacklinksHubResponse {
    total: number;
    posted: number;
    pending: number;
    backlinks: BacklinkHubItem[];
}

export const topicApi = createApi({
  reducerPath: 'topicApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_BASE_URL}/`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Forms', 'BacklinksHub'],
  endpoints: (builder) => ({
    getTopics: builder.query<TopicLink[], void>({
      query: () => 'forms',
      providesTags: ['Forms'],
    }),
    getFormById: builder.query<ITopic, string>({
        query: (id) => `forms/${id}`,
        providesTags: ['Forms'],
    }),
    getFormsBySprintId: builder.query<ITopic[], string>({
      query: (sprintId) => `forms/sprint/${sprintId}`,
      providesTags: ['Forms'],
    }),
    submitForm: builder.mutation<void, SubmitFormRequest>({
        query: (body) => ({
            url: 'forms/submit',
            method: 'POST',
            body,
        }),
        invalidatesTags: ['Forms'],
    }),
    publishForm: builder.mutation<PublishFormResponse, PublishFormRequest>({
        query: ({ formId, comments }) => ({
            url: `forms/publish/${formId}`,
            method: 'POST',
            body: comments ? { comments } : {},
        }),
        invalidatesTags: ['Forms'],
    }),
    approveForm: builder.mutation<void, string>({
        query: (formId) => ({
            url: `forms/approve/${formId}`,
            method: 'POST',
        }),
        invalidatesTags: ['Forms'],
    }),
    rejectForm: builder.mutation<void, string>({
        query: (formId) => ({
            url: `forms/reject/${formId}`,
            method: 'POST',
        }),
        invalidatesTags: ['Forms'],
    }),
    restartForm: builder.mutation<void, string>({
        query: (formId) => ({
            url: `forms/restart/${formId}`,
            method: 'POST',
        }),
        invalidatesTags: ['Forms'],
    }),
    getBacklinks: builder.query<BacklinksResponse, string>({
        query: (formId) => `forms/backlinks/${formId}`,
    }),
    getMyPublished: builder.query<MyPublishedBlog[], void>({
        query: () => 'forms/mine/published',
        providesTags: ['Forms'],
    }),
    getMyDrafts: builder.query<MyDraft[], void>({
        query: () => 'forms/mine/drafts',
        providesTags: ['Forms'],
    }),
    getAllPublishedBlogs: builder.query<AllPublishedBlog[], void>({
        query: () => 'forms/published-all',
        providesTags: ['Forms'],
    }),
    getBacklinksHub: builder.query<BacklinksHubResponse, void>({
        query: () => 'forms/backlinks-hub',
        providesTags: ['BacklinksHub'],
    }),
    toggleBacklinkPosted: builder.mutation<void, { formId: string; index: number; posted: boolean }>({
        query: ({ formId, index, posted }) => ({
            url: `forms/backlinks/${formId}/${index}`,
            method: 'PATCH',
            body: { posted },
        }),
        invalidatesTags: ['BacklinksHub'],
    }),
    regenerateBacklinks: builder.mutation<BacklinksResponse, string>({
        query: (formId) => ({
            url: `forms/backlinks/${formId}/regenerate`,
            method: 'POST',
        }),
        invalidatesTags: ['Forms', 'BacklinksHub'],
    }),
  }),
});

export const {
  useGetTopicsQuery,
  useGetFormByIdQuery,
  useGetFormsBySprintIdQuery,
  useSubmitFormMutation,
  usePublishFormMutation,
  useApproveFormMutation,
  useRejectFormMutation,
  useRestartFormMutation,
  useGetBacklinksQuery,
  useGetMyPublishedQuery,
  useGetMyDraftsQuery,
  useGetAllPublishedBlogsQuery,
  useGetBacklinksHubQuery,
  useToggleBacklinkPostedMutation,
  useRegenerateBacklinksMutation,
} = topicApi;
